import { type ReactNode, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useHistory, useLocation } from 'react-router-dom';

import { UIPreferenceProvider } from '@shared-legacy/contexts/UIPreferenceContext';
import { AppDesignFeatureGate } from '@app/design/featureFlags/appDesignFeatureFlags';
import {
  type AppDesignUrlOverride,
  clearAppDesignUrlOverrideHandoff,
  getAppDesignUrlOverride,
  readAppDesignUrlOverrideHandoff,
  removeAppDesignUrlOverrideParam,
  writeAppDesignUrlOverrideHandoff,
} from '@app/design/utils/appDesignUrlOverride';
import { EnvProvider } from '@shared/contexts';
import { evaluateFeatureFlags } from '@shared/featureFlags/evaluateFeatureFlags';
import { useCloudPlatformDiscovery } from '@shared/lib/hooks';
import { Loader } from '@shared/lib/ui/components-alpha';
import { useQueryEnvironmentData } from '@shared/services/appstore/appstore.hooks';

interface AppDesignBootstrapProps {
  children: ReactNode;
}

export const AppDesignBootstrap = (props: AppDesignBootstrapProps) => {
  const { children } = props;
  const auth = useAuth();
  const history = useHistory();
  const location = useLocation();
  const discovery = useCloudPlatformDiscovery();
  const urlOverride = getAppDesignUrlOverride(location.search);
  const [documentOverride, setDocumentOverride] = useState<AppDesignUrlOverride | undefined>(() => {
    if (urlOverride) {
      writeAppDesignUrlOverrideHandoff(urlOverride, location.pathname, location.hash);
      return urlOverride;
    }
    return readAppDesignUrlOverrideHandoff(location.pathname, location.hash);
  });
  const effectiveOverride = urlOverride ?? documentOverride;
  const environmentDataQuery = useQueryEnvironmentData({
    enabled: !auth.isLoading,
  });
  const evaluationContext = { env: environmentDataQuery.data, discovery };
  const forceModernDesign =
    evaluateFeatureFlags([AppDesignFeatureGate.NewDesignEnabled], evaluationContext).status === 'allowed';
  const designSwitchEnabled =
    evaluateFeatureFlags([AppDesignFeatureGate.SwitchEnabled], evaluationContext).status === 'allowed';

  useEffect(() => {
    if (!urlOverride) return;

    writeAppDesignUrlOverrideHandoff(urlOverride, location.pathname, location.hash);
    setDocumentOverride(urlOverride);

    // The parameter is taken out of the visible URL, and it has to be: navigation decides which
    // entry is active by comparing the whole path including the query string, so a stray
    // `?newDesign=` leaves nothing highlighted anywhere in the app. Per-item `ignoreSearch` /
    // `allowQueryKey` escapes exist, but a global parameter would need adding to every entry.
    // Removing it once is why the override then lives in state, and in session storage across
    // the sign-in redirect, rather than being re-read from the URL.
    const search = removeAppDesignUrlOverrideParam(location.search);
    history.replace({
      pathname: location.pathname,
      search: search ? `?${search}` : '',
      hash: location.hash,
      state: location.state,
    });
  }, [history, location.hash, location.pathname, location.search, location.state, urlOverride]);

  useEffect(() => {
    if (!effectiveOverride) return;
    if (auth.isAuthenticated || environmentDataQuery.data?.publicModeEnabled) {
      clearAppDesignUrlOverrideHandoff();
    }
  }, [auth.isAuthenticated, effectiveOverride, environmentDataQuery.data?.publicModeEnabled]);

  if (auth.isLoading || !environmentDataQuery.isFetched) {
    return <Loader label="Loading..." />;
  }

  if (environmentDataQuery.data) {
    return (
      <EnvProvider env={environmentDataQuery.data}>
        <UIPreferenceProvider
          designSwitchEnabled={designSwitchEnabled}
          documentOverride={effectiveOverride}
          forceModernDesign={forceModernDesign}
        >
          {children}
        </UIPreferenceProvider>
      </EnvProvider>
    );
  }

  if (auth.isAuthenticated) {
    throw environmentDataQuery.error ?? new Error('Failed to fetch environment data');
  }

  return <UIPreferenceProvider documentOverride={effectiveOverride}>{children}</UIPreferenceProvider>;
};
